/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * TICK TÍNH NĂNG + CỜ TUYẾN — bản của TRÌNH DUYỆT (`P-6` `B4`).
 *
 * ⛔ ĐÂY LÀ MỘT BẢN SAO CỦA `server/src/services/quyen.ts`, VÀ TÔI NÓI THẲNG RA THẾ.
 *   Dự án này đã cắt bốn lần cái bệnh "hai bản của một bảng" (`TEN_LENH` · bảng vai gõ tay ·
 *   `NGHIA_SO_KHONG` ↔ `.proto` · `lam_lai` gõ tay). Ở đây bản sao là **bắt buộc** — máy chủ
 *   và trình duyệt là hai chương trình, không nhập được của nhau — nên cái phải có là thứ
 *   thay cho "một nguồn": **một PHÉP CANH buộc hai bản khớp từng chữ**, ở mục `[93]` của
 *   `verify-protocol.ts`. Nó nhập CẢ HAI tệp và so.
 *   ⇒ Sửa một bên mà quên bên kia ⇒ bộ kiểm ĐỎ. Đó là toàn bộ lý do bản sao này được phép tồn tại.
 *
 * 🔴 VÀ ĐÂY KHÔNG PHẢI MỘT CỬA QUYỀN. Ẩn nút là **phép lịch sự với người dùng**; cửa thật là
 *   `requireQuyen` / `requireCoTuyen` ở máy chủ. Nếu hai bên lệch nhau thì người dùng thấy một
 *   cái nút bấm vào là 403 — khó chịu, nhưng KHÔNG mất an toàn. Nói ra để không ai đọc tệp này
 *   thành "đã có kiểm quyền rồi".
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
import type { Role } from './types';

/** Cờ mốc — hàng CHƯA ĐƯỢC ĐIỀN. `[]` là lời khai thật của `VIEWER`/`SUPER_ADMIN`. */
export const CHUA_DIEN = 'CHUA_DIEN';

/** Năm khoá TOÀN CỤC — *"làm gì"*. Nơi *"ở đâu"* là `RouteAccess`. */
export const KHOA_TICK = [
  'nhan_canh_bao',
  'chien_luoc',
  'doi_nguong',
  'quan_ly_tu',
  'tao_tai_khoan',
] as const;
export type KhoaTick = typeof KHOA_TICK[number];

/** Khoá LUÔN CÓ, không hiện trong bảng tick. */
export const KHOA_LUON_CO = 'xem_chan_doan' as const;

export const MOI_KHOA = [KHOA_LUON_CO, ...KHOA_TICK] as const;

/** Nhãn tiếng Việt — phải khớp từng chữ với `NHAN_KHOA` của máy chủ (phép canh `[93]`). */
export const NHAN_KHOA: Record<string, string> = {
  xem_chan_doan: 'Xem trạng thái · cảnh báo · nhật ký · sức khoẻ tủ',
  nhan_canh_bao: 'Tiếp nhận / xử lý cảnh báo',
  chien_luoc: 'Cài chiến lược',
  doi_nguong: 'Đổi ngưỡng bảo vệ, cài đặt tủ',
  quan_ly_tu: 'Thêm / sửa / xoá tủ và tuyến',
  tao_tai_khoan: 'Tạo và quản lý tài khoản phụ',
};

/** Câu MÔ TẢ cho người bấm — CHỈ có ở trình duyệt, máy chủ không cần (nên không phải bản sao). */
export const GIAI_THICH_KHOA: Record<string, string> = {
  nhan_canh_bao: 'Bấm “ghi nhận” một cảnh báo đang mở, và đóng popup mức 1.',
  chien_luoc: 'Tạo / sửa / xoá và áp chiến lược chiếu sáng.',
  doi_nguong: 'Đổi ngưỡng bảo vệ quá áp / thấp áp, cài đặt tủ, đồng bộ đồng hồ.',
  quan_ly_tu: 'Thêm, sửa, xoá tủ và tuyến — đụng tới cấu trúc, không chỉ vận hành.',
  tao_tai_khoan: 'Tạo tài khoản phụ, xem nhật ký thao tác của cả nhóm.',
};

export const CO_TUYEN = ['canControl', 'canEditSchedule'] as const;
export type CoTuyen = typeof CO_TUYEN[number];
export const NHAN_CO_TUYEN: Record<CoTuyen, string> = {
  canControl: 'Bật/tắt đèn trên tuyến này',
  canEditSchedule: 'Sửa lịch đóng cắt của tuyến này',
};

/**
 * BỘ TICK MẶC ĐỊNH THEO HẠNG — bản sao của `services/quyen.ts`, có phép canh `[93]` buộc khớp.
 *
 * 🔴 Form dùng nó để **đánh dấu sẵn** ô tick khi tạo tài khoản mới. Nhưng chỗ QUYẾT ĐỊNH vẫn ở
 *   MÁY CHỦ: `POST /api/users` không nhận trường `quyen` thì tự dùng bảng này. Form chỉ làm cho
 *   người bấm **NHÌN THẤY** cái mặc định trước khi bấm — nó không được là nơi duy nhất biết.
 * 📌 Đây chính là chỗ `Q10` phần B đỏ: form khởi tạo `[]`, máy chủ nhận `[]` và tôn trọng nó
 *   ⇒ `khach_thu` (Khách hàng) ra đời không có `tao_tai_khoan`, và menu Quản trị biến mất.
 */
export const TICK_MAC_DINH: Record<string, readonly string[]> = {
  SUPER_ADMIN: [],
  ADMIN: ['nhan_canh_bao', 'chien_luoc', 'doi_nguong', 'tao_tai_khoan'],
  MANAGER: ['nhan_canh_bao', 'chien_luoc', 'doi_nguong'],
  OPERATOR: ['nhan_canh_bao'],
  VIEWER: [],
};

export const laKhoaHopLe = (khoa: string): boolean => (MOI_KHOA as readonly string[]).includes(khoa);

export const chuaDienTick = (quyen: readonly string[] | null | undefined): boolean =>
  Array.isArray(quyen) && quyen.includes(CHUA_DIEN);

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * 🔴 `v4` `S1` — BẢN SAO của luật "tick chỉ dành cho bậc trên" (`server/src/services/quyen.ts`).
 *
 * ⛔ MỘT BẢN SAO CHỈ ĐƯỢC PHÉP TỒN TẠI KÈM MỘT PHÉP GHIM CẢ HAI BẢN — mục `[93]` so bảng, so
 *   nhãn và so **HÀNH VI** của hai tệp này. Ở đây có ba thứ phải khớp: cái trần bậc, danh sách
 *   hai tick, và thứ tự các vế trong `coQuyen()`.
 * ⚠ Vì sao vẫn phải sao: form cần khoá hai ô NGAY LÚC GÕ, trước khi gửi. Bắt người bấm dựng
 *   xong một tài khoản rồi mới ăn 403 là một cách nói *"tôi biết trước mà không nói"*.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
const BAC_MAP = new Map<Role, number>([
  ['SUPER_ADMIN', 0], ['ADMIN', 1], ['MANAGER', 2], ['OPERATOR', 3], ['VIEWER', 3],
]);
const TRAN_BAC_CUA_TICK = new Map<string, number>([
  ['quan_ly_tu', 1],
  ['tao_tai_khoan', 1],
]);
export const TICK_CHI_BAC_TREN: readonly string[] = [...TRAN_BAC_CUA_TICK.keys()];

export const bacCuaHang = (vai: Role): number => BAC_MAP.get(vai) ?? 3;

/** `true` ⇔ hạng này được phép GIỮ khoá ấy. */
export function tickHopVoiHang(vai: Role | null | undefined, khoa: string): boolean {
  if (!vai) return false;
  const tran = TRAN_BAC_CUA_TICK.get(khoa);
  if (tran === undefined) return true;
  return bacCuaHang(vai) <= tran;
}

export function tickCamTheoHang(vai: Role | null | undefined, ds: readonly string[]): string[] {
  return ds.filter((k) => k !== CHUA_DIEN && !tickHopVoiHang(vai, k));
}

/** Tên bậc như lee gọi — form in ra câu *"không dành cho bậc Vận hành"*. */
export const TEN_BAC: Record<string, string> = {
  SUPER_ADMIN: 'Quản trị hệ thống',
  ADMIN: 'Khách hàng',
  MANAGER: 'Vận hành',
  OPERATOR: 'Vận hành (hạng cũ)',
  VIEWER: 'Chỉ xem (hạng cũ)',
};

/** Câu giải thích — một chỗ viết, cả form lẫn huy hiệu đọc. */
export const cauTickKhongDanhChoHang = (vai: Role, khoa: string): string =>
  `“${NHAN_KHOA[khoa] ?? khoa}” không dành cho bậc ${TEN_BAC[vai] ?? vai}`;

/**
 * 🔴 CÙNG BỐN VẾ, CÙNG THỨ TỰ với máy chủ — và thứ tự là một phần của luật, không phải cách
 *   viết cho gọn: phép kiểm **khoá lạ** đứng TRƯỚC vế `SUPER_ADMIN`. Đảo hai dòng thì một
 *   khoá gõ sai thành "luôn có" cho quản trị viên cao nhất, và cái nút hiện ra ở đúng chỗ nó
 *   không được hiện.
 */
export function coQuyen(
  vai: Role | null | undefined,
  khoa: string,
  tick: readonly string[] | null | undefined,
): boolean {
  if (!laKhoaHopLe(khoa)) return false;
  if (khoa === KHOA_LUON_CO) return !!vai;
  if (!vai) return false;
  if (vai === 'SUPER_ADMIN') return true;
  if (chuaDienTick(tick)) return false;
  /* ⓹ `v4` `S1` — tick không dành cho bậc này ⇒ `false`, dù cột dữ liệu có nó. */
  if (!tickHopVoiHang(vai, khoa)) return false;
  return Array.isArray(tick) && tick.includes(khoa);
}

export type QuyetDinhCoTuyen = 'cho-qua' | 'chua-gan-tuyen' | 'khong-co-co';
export function quyetDinhCoTuyen(
  vai: Role | null | undefined,
  co: CoTuyen,
  hang: { canControl: boolean; canEditSchedule: boolean } | null | undefined,
): QuyetDinhCoTuyen {
  if (vai === 'SUPER_ADMIN') return 'cho-qua';
  if (!vai) return 'khong-co-co';
  if (!hang) return 'chua-gan-tuyen';
  return hang[co] ? 'cho-qua' : 'khong-co-co';
}

/**
 * CÂU cho người bị chặn ở một nút — ba ca, ba câu, và chúng dẫn tới ba chỗ khác nhau.
 * ⚠ `routeId` rỗng (tủ chưa gán tuyến) là ca THỨ TƯ và nó không phải chuyện quyền: nói đúng
 *   như thế, đừng đổ cho tài khoản.
 */
export function cauChanCoTuyen(qd: QuyetDinhCoTuyen | 'khong-co-tuyen', co: CoTuyen): string {
  if (qd === 'khong-co-tuyen') return 'Tủ này chưa được gán tuyến nào — chưa ai điều khiển được nó.';
  if (qd === 'chua-gan-tuyen') return 'Tài khoản của bạn chưa được gán tuyến này. Quản trị viên cao nhất gán tuyến.';
  if (qd === 'khong-co-co') return `Trên tuyến này, tài khoản của bạn không có quyền “${NHAN_CO_TUYEN[co]}”.`;
  return '';
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * HAI HẠNG NGƯỜI DÙNG THẤY TRÊN MÀN HÌNH — `A.6`, và đây là chỗ 5 vai của CSDL gặp 2 chữ của lee.
 *
 * lee nói chuyện bằng hai chữ: **Khách hàng** (khách chính) và **Vận hành** (quản lý phụ).
 * CSDL thì có 5 vai. Ánh xạ đã chốt 09/09 (`services/anhxavai.ts`): `main_custom → ADMIN`,
 * `Co-main_custom → MANAGER`.
 *
 * ⛔ CHỈ HAI HẠNG NÀY HIỆN TRONG FORM — `SUPER_ADMIN` không bao giờ tạo được từ giao diện
 *   (máy chủ cũng cấm: `duocPhepDatVai` đòi THẤP HƠN HẲN), còn `OPERATOR`/`VIEWER` là vai
 *   LỊCH SỬ của bản trước. Không xoá chúng khỏi CSDL (hàng cũ còn mang), nhưng không đẻ thêm.
 * ⚠ Và **khách tạo tài khoản thì hạng bị CỐ ĐỊNH là Vận hành** — lee chốt. Không phải một ô
 *   chọn bị vô hiệu hoá cho đẹp: form không cho chọn, và máy chủ vẫn chặn độc lập.
 */
export const HANG_HIEN = [
  { vai: 'ADMIN' as Role, ten: 'Khách hàng', mo: 'Khách chính — tạo được tài khoản Vận hành của riêng mình.' },
  { vai: 'MANAGER' as Role, ten: 'Vận hành', mo: 'Quản lý phụ — làm việc trong phạm vi khách chính giao.' },
];
export const tenHang = (vai: Role | null | undefined): string =>
  vai === 'SUPER_ADMIN' ? 'Quản trị hệ thống'
  : HANG_HIEN.find((h) => h.vai === vai)?.ten ?? String(vai ?? '—');

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * GÓI THỜI HẠN — lee đặt hàng: *Vĩnh viễn · 1 · 3 · 6 tháng · 1 · 2 · 3 · 5 năm*.
 *
 * 🔴 `null` = VĨNH VIỄN là một **mốc thật**, không phải "chưa chọn gì". Nên gói đầu tiên mang
 *   `thang: null` chứ không phải `0` — `0 tháng` sẽ được đọc thành "hết hạn ngay", và đó đúng
 *   là luật số 0 của dự án lần nữa, lần này ở một ô chọn.
 * ⚠ MỐC TÍNH TỪ **BÂY GIỜ**, tính ở lúc bấm, không phải lúc mở form: một form mở từ sáng mà
 *   bấm lúc chiều thì hai mốc lệch nhau vài tiếng — nhỏ, nhưng nó là loại lệch không ai đi tìm.
 */
export const GOI_HAN: { ten: string; thang: number | null }[] = [
  { ten: 'Vĩnh viễn', thang: null },
  { ten: '1 tháng', thang: 1 },
  { ten: '3 tháng', thang: 3 },
  { ten: '6 tháng', thang: 6 },
  { ten: '1 năm', thang: 12 },
  { ten: '2 năm', thang: 24 },
  { ten: '3 năm', thang: 36 },
  { ten: '5 năm', thang: 60 },
];

/** Cộng tháng vào MỘT mốc cho trước — thuần, nhận `bayGio` để đo được. */
export function hanTheoGoi(thang: number | null, bayGio: Date): Date | null {
  if (thang === null) return null;
  const d = new Date(bayGio.getTime());
  d.setMonth(d.getMonth() + thang);
  return d;
}

/**
 * Đọc một mốc hết hạn thành câu + mức cảnh báo. Thuần, nhận `bayGioMs`.
 *
 * ⛔ BA MỨC, KHÔNG PHẢI HAI: `het` (đã qua) · `sap` (dưới 30 ngày) · `con`. Gộp `sap` vào `con`
 *   thì khách phát hiện ra hạn đúng vào ngày họ không đăng nhập được nữa.
 */
export function docHan(
  hetHan: string | Date | null | undefined, bayGioMs: number,
): { muc: 'vinh-vien' | 'het' | 'sap' | 'con'; cau: string; soNgay: number | null } {
  if (hetHan === null || hetHan === undefined) return { muc: 'vinh-vien', cau: 'Vĩnh viễn', soNgay: null };
  const t = hetHan instanceof Date ? hetHan.getTime() : Date.parse(String(hetHan));
  if (!Number.isFinite(t)) return { muc: 'vinh-vien', cau: 'Vĩnh viễn', soNgay: null };
  const ngay = Math.ceil((t - bayGioMs) / 86400000);
  if (t <= bayGioMs) return { muc: 'het', cau: 'Đã hết hạn', soNgay: ngay };
  if (ngay <= 30) return { muc: 'sap', cau: `Còn ${ngay} ngày`, soNgay: ngay };
  return { muc: 'con', cau: `Còn ${ngay} ngày`, soNgay: ngay };
}
